import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo, FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { LuPanelLeft } from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';
import PortalIconPicker from '../../../components/PortalCMS/PortalIconPicker';

const SLUG = 'portal-sidebar';

const emptyNavItem = (order) => ({
  key: '',
  route: '',
  label: '',
  icon: 'LuLayoutDashboard',
  isActive: true,
  order,
});

const defaultForm = () => ({
  isPublished: true,
  logo: {
    lightUrl: '',
    darkUrl: '',
    altText: 'BooksComply',
  },
  mainNav: [],
  servicesSectionTitle: 'Services',
  serviceNav: [],
  referAndEarn: {
    title: 'Refer & Earn',
    subtitle: 'Get 1000 Points!',
    icon: 'LuGift',
    route: '/refer-and-earn',
    isActive: true,
  },
  assigneeNav: {
    label: 'Bookkeeping',
    icon: 'LuBookOpenCheck',
    route: '/bookkeeping',
  },
});

const ListControls = ({ onUp, onDown, onRemove, disableUp, disableDown }) => (
  <div className="flex items-center gap-1">
    <button type="button" onClick={onUp} disabled={disableUp} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowUp className="w-3 h-3" /></button>
    <button type="button" onClick={onDown} disabled={disableDown} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowDown className="w-3 h-3" /></button>
    <button type="button" onClick={onRemove} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><FaTrash className="w-3.5 h-3.5" /></button>
  </div>
);

const SidebarPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const c = res.data.data.content || {};
        setForm({
          isPublished: res.data.data.isPublished !== false,
          logo: { ...defaultForm().logo, ...(c.logo || {}) },
          mainNav: (c.mainNav || []).map((item, i) => ({ ...item, order: item.order ?? i })),
          servicesSectionTitle: c.servicesSectionTitle || 'Services',
          serviceNav: (c.serviceNav || []).map((item, i) => ({ ...item, order: item.order ?? i })),
          referAndEarn: { ...defaultForm().referAndEarn, ...(c.referAndEarn || {}) },
          assigneeNav: { ...defaultForm().assigneeNav, ...(c.assigneeNav || {}) },
        });
      }
    } catch {
      toastError('Failed to load sidebar content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const moveList = (listKey, index, dir) => {
    setForm((prev) => {
      const list = [...prev[listKey]];
      const next = index + dir;
      if (next < 0 || next >= list.length) return prev;
      [list[index], list[next]] = [list[next], list[index]];
      return { ...prev, [listKey]: list.map((item, i) => ({ ...item, order: i })) };
    });
  };

  const updateListItem = (listKey, index, field, value) => {
    setForm((prev) => {
      const list = [...prev[listKey]];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [listKey]: list };
    });
  };

  const addListItem = (listKey) => {
    setForm((prev) => ({
      ...prev,
      [listKey]: [...prev[listKey], emptyNavItem(prev[listKey].length)],
    }));
  };

  const removeListItem = (listKey, index) => {
    setForm((prev) => ({
      ...prev,
      [listKey]: prev[listKey].filter((_, i) => i !== index).map((item, i) => ({ ...item, order: i })),
    }));
  };

  const updateLogo = (field, value) => {
    setForm((prev) => ({
      ...prev,
      logo: { ...prev.logo, [field]: value },
    }));
  };

  const uploadLogo = async (field, file) => {
    if (!file) return;
    setUploadingLogo(field);
    try {
      const token = localStorage.getItem('adminToken');
      const data = new FormData();
      data.append('file', file);
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/upload`, data, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        updateLogo(field, res.data.data.fileUrl);
        toastSuccess('Logo uploaded');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingLogo(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        ...form,
        mainNav: form.mainNav.map((item, i) => ({ ...item, order: i })),
        serviceNav: form.serviceNav.map((item, i) => ({ ...item, order: i })),
      };
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: payload, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess('Sidebar saved');
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded sidebar defaults?')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Defaults restored');
      fetchContent();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  const renderNavList = (listKey, title) => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-bold">{title}</h3>
        <button type="button" onClick={() => addListItem(listKey)} className="text-sm text-primary-600 flex items-center gap-1"><FaPlus /> Add link</button>
      </div>
      {form[listKey].map((item, index) => (
        <div key={`${listKey}-${index}`} className="rounded-2xl border p-4 space-y-3 bg-gray-50/50 dark:bg-dark-900/30">
          <div className="flex justify-between">
            <span className="text-sm font-bold">Link {index + 1}</span>
            <ListControls
              onUp={() => moveList(listKey, index, -1)}
              onDown={() => moveList(listKey, index, 1)}
              onRemove={() => removeListItem(listKey, index)}
              disableUp={index === 0}
              disableDown={index === form[listKey].length - 1}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="input-modern" value={item.key} onChange={(e) => updateListItem(listKey, index, 'key', e.target.value)} placeholder="Key (e.g. dashboard)" />
            <input className="input-modern" value={item.route} onChange={(e) => updateListItem(listKey, index, 'route', e.target.value)} placeholder="Route (e.g. /dashboard)" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="input-modern" value={item.label} onChange={(e) => updateListItem(listKey, index, 'label', e.target.value)} placeholder="Label" />
            <PortalIconPicker value={item.icon} onChange={(icon) => updateListItem(listKey, index, 'icon', icon)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={item.isActive !== false} onChange={(e) => updateListItem(listKey, index, 'isActive', e.target.checked)} />
            Active
          </label>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader count={4} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Sidebar"
        description="Edit sidebar logo, main navigation, services links, Refer & Earn, and assignee sidebar."
        icon={LuPanelLeft}
        actions={
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate('/portal-cms/sidebar-header')} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm">Back</button>
            <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border text-sm flex items-center gap-2">
              <FaUndo className="w-3.5 h-3.5" /> Reset
            </button>
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium text-sm py-2 px-4 flex items-center gap-2">
              <FaSave className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      <div className="space-y-8">
        <label className="flex items-center gap-2 text-sm bg-white dark:bg-dark-800 rounded-2xl border p-4">
          <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))} />
          Published
        </label>

        <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
          <h3 className="font-bold">Sidebar logo</h3>
          <p className="text-xs text-gray-500">Upload logos for light and dark mode. Leave empty to use the default BooksComply logo.</p>
          <input
            className="input-modern"
            value={form.logo?.altText || ''}
            onChange={(e) => updateLogo('altText', e.target.value)}
            placeholder="Logo alt text (accessibility)"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 p-4 rounded-xl border border-gray-100 dark:border-dark-700">
              <p className="text-sm font-semibold">Light mode logo</p>
              {form.logo?.lightUrl && (
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}${form.logo.lightUrl}`}
                  alt="Light logo preview"
                  className="h-10 object-contain bg-gray-50 rounded p-2"
                />
              )}
              <input
                className="input-modern"
                value={form.logo?.lightUrl || ''}
                onChange={(e) => updateLogo('lightUrl', e.target.value)}
                placeholder="/uploads/portal/logo-light.png"
              />
              <label className="inline-flex items-center gap-2 text-sm text-primary-600 cursor-pointer">
                {uploadingLogo === 'lightUrl' ? 'Uploading...' : 'Upload light logo'}
                <input type="file" accept="image/*,.svg" className="hidden" onChange={(e) => uploadLogo('lightUrl', e.target.files?.[0])} />
              </label>
            </div>
            <div className="space-y-2 p-4 rounded-xl border border-gray-100 dark:border-dark-700">
              <p className="text-sm font-semibold">Dark mode logo</p>
              {form.logo?.darkUrl && (
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}${form.logo.darkUrl}`}
                  alt="Dark logo preview"
                  className="h-10 object-contain bg-dark-900 rounded p-2"
                />
              )}
              <input
                className="input-modern"
                value={form.logo?.darkUrl || ''}
                onChange={(e) => updateLogo('darkUrl', e.target.value)}
                placeholder="/uploads/portal/logo-dark.png"
              />
              <label className="inline-flex items-center gap-2 text-sm text-primary-600 cursor-pointer">
                {uploadingLogo === 'darkUrl' ? 'Uploading...' : 'Upload dark logo'}
                <input type="file" accept="image/*,.svg" className="hidden" onChange={(e) => uploadLogo('darkUrl', e.target.files?.[0])} />
              </label>
            </div>
          </div>
        </div>

        {renderNavList('mainNav', 'Main navigation')}
        <p className="text-xs text-gray-500 -mt-2">Account Settings dropdown is inserted automatically after the Help Guide link.</p>

        <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-3">
          <h3 className="font-bold">Services section</h3>
          <input className="input-modern" value={form.servicesSectionTitle} onChange={(e) => setForm((prev) => ({ ...prev, servicesSectionTitle: e.target.value }))} placeholder="Section title" />
        </div>

        {renderNavList('serviceNav', 'Service links')}

        <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
          <h3 className="font-bold">Refer & Earn button</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="input-modern" value={form.referAndEarn.title} onChange={(e) => setForm((prev) => ({ ...prev, referAndEarn: { ...prev.referAndEarn, title: e.target.value } }))} placeholder="Title" />
            <input className="input-modern" value={form.referAndEarn.subtitle} onChange={(e) => setForm((prev) => ({ ...prev, referAndEarn: { ...prev.referAndEarn, subtitle: e.target.value } }))} placeholder="Subtitle" />
          </div>
          <input className="input-modern" value={form.referAndEarn.route} onChange={(e) => setForm((prev) => ({ ...prev, referAndEarn: { ...prev.referAndEarn, route: e.target.value } }))} placeholder="Route" />
          <PortalIconPicker value={form.referAndEarn.icon} onChange={(icon) => setForm((prev) => ({ ...prev, referAndEarn: { ...prev.referAndEarn, icon } }))} />
        </div>

        <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
          <h3 className="font-bold">Assignee sidebar (limited access users)</h3>
          <input className="input-modern" value={form.assigneeNav.label} onChange={(e) => setForm((prev) => ({ ...prev, assigneeNav: { ...prev.assigneeNav, label: e.target.value } }))} placeholder="Label" />
          <input className="input-modern" value={form.assigneeNav.route} onChange={(e) => setForm((prev) => ({ ...prev, assigneeNav: { ...prev.assigneeNav, route: e.target.value } }))} placeholder="Route" />
          <PortalIconPicker value={form.assigneeNav.icon} onChange={(icon) => setForm((prev) => ({ ...prev, assigneeNav: { ...prev.assigneeNav, icon } }))} />
        </div>
      </div>
    </PageLayout>
  );
};

export default SidebarPortalCMS;
